import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { autoPostAPI, templateAPI } from '../lib/api';
import { 
  Zap, Clock, Calendar, Plus, Trash2, CheckCircle,
  AlertTriangle, Megaphone, Info, PartyPopper, Bell, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const AutoPostSettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const times = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    return [`${hour}:00`, `${hour}:30`];
  }).flat();
  
  const postTypes = [
    { value: 'standard', icon: <Megaphone className="w-4 h-4" />, label: 'Standard' },
    { value: 'event', icon: <Info className="w-4 h-4" />, label: 'Event' },
    { value: 'offer', icon: <PartyPopper className="w-4 h-4" />, label: 'Offer' },
  ];

  const ctaOptions = [
    { value: 'none', label: 'None' },
    { value: 'learn_more', label: 'Learn more' },
    { value: 'book', label: 'Book a visit' },
    { value: 'order', label: 'Place an order' },
    { value: 'shop', label: 'Shop' },
    { value: 'sign_up', label: 'Sign up' },
    { value: 'call', label: 'Call' },
  ];

  const toneOptions = ['Professional', 'Friendly', 'Promotional', 'Informative', 'Casual'];
  const languageOptions = ['English', 'Hindi', 'Spanish', 'French', 'German'];

  useEffect(() => {
    fetchSettings();
    templateAPI.getSelected().then(res => setSelectedTemplates(res.data.templates || [])).catch(() => {});
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await autoPostAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      await autoPostAPI.updateSettings({
        enabled,
        schedules: settings.schedules || []
      });
      setSettings({ ...settings, enabled });
      toast.success(enabled ? 'Auto-post enabled!' : 'Auto-post disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const addSchedule = () => {
    const newSchedules = [...(settings.schedules || []), { day: 'monday', time: '10:00', post_type: 'standard', cta: 'none', language: 'English', tone: 'Professional' }];
    setSettings({ ...settings, schedules: newSchedules });
  };

  const removeSchedule = (index) => {
    const newSchedules = settings.schedules.filter((_, i) => i !== index);
    setSettings({ ...settings, schedules: newSchedules });
  };

  const updateSchedule = (index, field, value) => {
    const newSchedules = [...settings.schedules];
    newSchedules[index] = { ...newSchedules[index], [field]: value };
    setSettings({ ...settings, schedules: newSchedules });
  };

  const saveSchedules = async () => {
    setSaving(true);
    try {
      await autoPostAPI.updateSettings({
        enabled: settings.enabled,
        schedules: settings.schedules || []
      });
      toast.success('Schedules saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save schedules');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async (postType) => {
    setGenerating(true);
    try {
      const response = await autoPostAPI.generateNow(postType);
      toast.success('Post generated and saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate post');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Check if user can use auto-post
  if (!settings?.can_use_auto_post) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#FEF7E0] rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-[#F9AB00]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Auto-Post Settings
            </h1>
            <p className="text-[#5F6368]">Automatically generate and post to your GMB</p>
          </div>
        </div>

        <div className="dashboard-card text-center py-12">
          <AlertTriangle className="w-16 h-16 mx-auto text-[#F9AB00]" />
          <h2 className="mt-4 text-xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Upgrade to Unlock Auto-Post
          </h2>
          <p className="mt-2 text-[#5F6368] max-w-md mx-auto">
            Auto-posting is available on Basic and Pro plans. Upgrade to automatically generate and publish posts according to your schedule.
          </p>
          <Link to="/pricing" className="btn-google inline-block mt-6">
            View Plans
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#E8F0FE] rounded-xl flex items-center justify-center">
            <Zap className="w-6 h-6 text-[#1A73E8]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
              Auto-Post Settings
            </h1>
            <p className="text-[#5F6368]">
              {settings.posts_remaining_month} posts remaining this month
            </p>
          </div>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              settings.enabled ? 'bg-[#E6F4EA]' : 'bg-[#F1F3F4]'
            }`}>
              {settings.enabled ? (
                <CheckCircle className="w-6 h-6 text-[#1E8E3E]" />
              ) : (
                <Zap className="w-6 h-6 text-[#5F6368]" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-[#202124]">Auto-Post</h3>
              <p className="text-sm text-[#5F6368]">
                {settings.enabled 
                  ? 'Posts will be automatically generated and published' 
                  : 'Enable to start automatic posting'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle(!settings.enabled)}
            disabled={saving}
            className={`w-14 h-7 rounded-full transition-colors ${
              settings.enabled ? 'bg-[#1A73E8]' : 'bg-[#DADCE0]'
            }`}
            data-testid="auto-post-toggle"
          >
            <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${
              settings.enabled ? 'translate-x-7' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>

      {/* Schedules */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#202124]" style={{ fontFamily: 'Outfit' }}>
            Post Schedule
          </h3>
          <button
            onClick={addSchedule}
            className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
            data-testid="add-schedule-btn"
          >
            <Plus className="w-4 h-4" /> Add Schedule
          </button>
        </div>

        {(!settings.schedules || settings.schedules.length === 0) ? (
          <div className="text-center py-8 text-[#5F6368]">
            <Calendar className="w-12 h-12 mx-auto text-[#DADCE0]" />
            <p className="mt-4">No schedules set. Add a schedule to start auto-posting.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {settings.schedules.map((schedule, index) => (
              <div key={index} className="p-4 bg-[#F8F9FA] rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#5F6368]" />
                    <span className="text-sm font-medium text-[#202124]">Schedule {index + 1}</span>
                  </div>
                  <button
                    onClick={() => removeSchedule(index)}
                    className="p-1.5 text-[#5F6368] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-lg"
                    data-testid={`remove-schedule-${index}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">Day</label>
                    <select
                      value={schedule.day}
                      onChange={(e) => updateSchedule(index, 'day', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-day-${index}`}
                    >
                      {days.map((day) => (
                        <option key={day} value={day}>
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">Time</label>
                    <select
                      value={schedule.time}
                      onChange={(e) => updateSchedule(index, 'time', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-time-${index}`}
                    >
                      {times.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">Type</label>
                    <select
                      value={schedule.post_type}
                      onChange={(e) => updateSchedule(index, 'post_type', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-type-${index}`}
                    >
                      {postTypes.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">CTA</label>
                    <select
                      value={schedule.cta || 'none'}
                      onChange={(e) => updateSchedule(index, 'cta', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-cta-${index}`}
                    >
                      {ctaOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">Language</label>
                    <select
                      value={schedule.language || 'English'}
                      onChange={(e) => updateSchedule(index, 'language', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-language-${index}`}
                    >
                      {languageOptions.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-[#5F6368] mb-1">Tone</label>
                    <select
                      value={schedule.tone || 'Professional'}
                      onChange={(e) => updateSchedule(index, 'tone', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-tone-${index}`}
                    >
                      {toneOptions.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Template selector */}
                {selectedTemplates.length > 0 && (
                  <div className="mt-2">
                    <label className="block text-xs text-[#5F6368] mb-1"><Palette className="w-3 h-3 inline mr-1" />Creative Template (optional)</label>
                    <select
                      value={schedule.template_id || ''}
                      onChange={(e) => updateSchedule(index, 'template_id', e.target.value)}
                      className="input-google py-2 w-full text-sm"
                      data-testid={`schedule-template-${index}`}
                    >
                      <option value="">No template (AI default)</option>
                      {selectedTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {settings.schedules && settings.schedules.length > 0 && (
          <button
            onClick={saveSchedules}
            disabled={saving}
            className="btn-google mt-4"
            data-testid="save-schedules-btn"
          >
            {saving ? 'Saving...' : 'Save Schedules'}
          </button>
        )}
      </div>

      {/* Manual Generate */}
      <div className="dashboard-card">
        <h3 className="text-lg font-semibold text-[#202124] mb-4" style={{ fontFamily: 'Outfit' }}>
          Generate Post Now
        </h3>
        <p className="text-sm text-[#5F6368] mb-4">
          Manually trigger a post generation for testing or immediate posting.
        </p>
        <div className="flex flex-wrap gap-3">
          {postTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => generateNow(type.value)}
              disabled={generating}
              className="btn-outline text-sm py-2 px-4 flex items-center gap-2"
              data-testid={`generate-now-${type.value}`}
            >
              {type.icon}
              {generating ? 'Generating...' : type.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AutoPostSettingsPage;
